import { ChevronDown, ChevronRight, Search } from "lucide-react"
import { Fragment, useState } from "react"

import {
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@atlant/ui"

import { formatAmount, sortTotals } from "#utils/money.ts"

import type { CategoryGroup } from "../page.tsx"
import { CategoryInvoices } from "./category-invoices.tsx"

const groupKey = (group: CategoryGroup) => group.categoryId ?? "uncategorized"

interface InvoiceInboxProps {
  groups: CategoryGroup[]
  isLoading: boolean
  selectedId?: string
  onSelect: (invoiceId: string) => void
  emptyText: string
}

export const InvoiceInbox = ({
  groups,
  isLoading,
  selectedId,
  onSelect,
  emptyText,
}: InvoiceInboxProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState("")

  // The selected invoice's group opens: when a link selects it, and when an edit moves it
  const selectedGroup = groups.find((group) =>
    group.invoices.some(({ id }) => id === selectedId),
  )
  const selectedPlace = selectedGroup && `${selectedId}|${groupKey(selectedGroup)}`
  const [openedFor, setOpenedFor] = useState<string>()
  if (selectedGroup && selectedPlace !== openedFor) {
    setOpenedFor(selectedPlace)
    setExpanded((prev) => new Set(prev).add(groupKey(selectedGroup)))
  }

  const term = search.trim().toLowerCase()
  const visible = groups
    .map((group) => ({
      group,
      invoices: term
        ? group.invoices.filter(
            ({ vendor, number }) =>
              vendor.toLowerCase().includes(term) || number.toLowerCase().includes(term),
          )
        : group.invoices,
    }))
    .filter(({ invoices }) => !term || invoices.length > 0)

  const invoiceCount = groups.reduce((sum, group) => sum + group.invoiceCount, 0)

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })

  const headClass = "text-muted-foreground h-10 text-xs font-medium uppercase"

  return (
    <div className="bg-card flex flex-col rounded-lg border lg:min-h-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div className="flex items-center gap-2 text-sm whitespace-nowrap">
          <span className="font-semibold">Invoice Inbox</span>
          <span className="text-muted-foreground">
            · {invoiceCount} invoices · {groups.length} categories
          </span>
        </div>
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search Invoice"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-48 border-none bg-transparent pl-9 text-sm shadow-none"
          />
        </div>
      </div>

      <div className="overflow-auto lg:min-h-0 lg:flex-1">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className={`${headClass} w-8`} />
              <TableHead className={headClass}>Code</TableHead>
              <TableHead className={headClass}>Category</TableHead>
              <TableHead className={`${headClass} text-center`}>Invoices</TableHead>
              <TableHead className={`${headClass} text-right`}>Total amount, €</TableHead>
              <TableHead className={`${headClass} w-6`} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 6 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                  {term ? "No invoices match your search" : emptyText}
                </TableCell>
              </TableRow>
            )}

            {visible.map(({ group, invoices }) => {
              const key = groupKey(group)
              const isOpen = !!term || expanded.has(key)
              const [primary, ...other] = sortTotals(group.totals)
              const Chevron = isOpen ? ChevronDown : ChevronRight

              return (
                <Fragment key={key}>
                  <TableRow
                    className="hover:bg-muted/30 cursor-pointer"
                    onClick={() => toggle(key)}
                  >
                    <TableCell className="w-8 pr-0">
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-label={`Toggle ${group.description ?? "Uncategorized"}`}
                        className="text-muted-foreground flex items-center"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggle(key)
                        }}
                      >
                        <Chevron className="size-4" />
                      </button>
                    </TableCell>
                    <TableCell className="font-medium">{group.code ?? "—"}</TableCell>
                    {/* Wraps, so long names leave room for the amounts in a half-width panel */}
                    <TableCell className="whitespace-normal">
                      {group.description ?? "Uncategorized"}
                    </TableCell>
                    <TableCell className="text-center">{group.invoiceCount}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {primary &&
                        formatAmount(
                          primary.amount,
                          primary.currency === "EUR" ? undefined : primary.currency,
                        )}
                      {other.map(({ currency, amount }) => (
                        <div
                          key={currency}
                          className="text-muted-foreground text-xs font-normal"
                        >
                          + {formatAmount(amount, currency)}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell className="w-6">
                      {group.lateCount > 0 && (
                        <span
                          className="bg-destructive block size-2.5 rounded-full"
                          title={`${group.lateCount} late`}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={6} className="p-0 pb-2 pl-8">
                        <CategoryInvoices
                          invoices={invoices}
                          selectedId={selectedId}
                          onSelect={onSelect}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
